import {
  AlertIcon,
  CurecallButton,
  CurecallList,
  CurecallListHeaders,
  CurecallPaginationBar,
  CurecallTooltip,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { EHRDispatchStatus, THRCampaignDispatch } from 'src/types/hr'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { twMerge } from 'src/utils/tw-merge.util'
import { DispatchSmsLogsModal } from '../../_components/dispatch-sms-logs-modal.component'
import { formatPhone } from 'src/utils/phone.util'

type TDispatchHeaderName = 'Lastname' | 'Firstname' | 'Phone' | 'Status' | 'Actions'
type TTableRow = { [key in TDispatchHeaderName]: ReactNode }
type TSortState = { key: TDispatchHeaderName; descOrder: boolean }

const DISPATCH_STATUS_CLASSES: Record<EHRDispatchStatus, string> = {
  PENDING: 'c-bg-yellow-100 c-text-yellow-700',
  COMPLETED: 'c-bg-green-100 c-text-green-700',
  SUPERSEDED: 'c-bg-grey-100 c-text-grey-500',
  UNREACHABLE: 'c-bg-red-100 c-text-red-700',
}

const ITEMS_PER_PAGE = 50

export const AlertCampaignDispatchesTable = ({
  dispatches,
  campaignId,
}: Readonly<{
  dispatches: THRCampaignDispatch[]
  campaignId: number
}>) => {
  const { t } = useTranslation()
  const [selectedDispatch, setSelectedDispatch] = useState<THRCampaignDispatch | null>(null)
  const [sort, setSort] = useState<TSortState>({ key: 'Lastname', descOrder: false })
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [dispatches])

  const handleSort = (key: TDispatchHeaderName) => {
    setSort((prev) => (prev.key === key ? { key, descOrder: !prev.descOrder } : { key, descOrder: false }))
    setCurrentPage(1)
  }

  const sortedDispatches = useMemo(() => {
    return [...dispatches].sort((a, b) => {
      let cmp = 0
      if (sort.key === 'Lastname') {
        cmp = (a.HRContact?.lastname ?? '').localeCompare(b.HRContact?.lastname ?? '', 'fr')
      } else if (sort.key === 'Firstname') {
        cmp = (a.HRContact?.firstname ?? '').localeCompare(b.HRContact?.firstname ?? '', 'fr')
      } else if (sort.key === 'Status') {
        cmp = a.status.localeCompare(b.status)
      }
      return sort.descOrder ? -cmp : cmp
    })
  }, [dispatches, sort])

  const dispatchStatusLabels: Record<EHRDispatchStatus, string> = {
    [EHRDispatchStatus.PENDING]: t('pages.hr.campaigns.detail.dispatch-status.PENDING'),
    [EHRDispatchStatus.COMPLETED]: t('pages.hr.campaigns.detail.dispatch-status.completed-alert'),
    [EHRDispatchStatus.SUPERSEDED]: t('pages.hr.campaigns.detail.dispatch-status.SUPERSEDED'),
    [EHRDispatchStatus.UNREACHABLE]: t('pages.hr.campaigns.detail.dispatch-status.UNREACHABLE'),
  }

  const rows = sortedDispatches.map((dispatch) => {
    const statusBadge = (
      <span
        className={twMerge(
          'c-px-2 c-py-0.5 c-rounded-full c-text-xs c-font-medium',
          DISPATCH_STATUS_CLASSES[dispatch.status],
        )}
      >
        {dispatchStatusLabels[dispatch.status]}
      </span>
    )

    return {
      Lastname: dispatch.HRContact?.lastname ?? '—',
      Firstname: dispatch.HRContact?.firstname ?? '—',
      Phone: (
        <span className="c-flex c-items-center c-gap-1">
          {dispatch.HRContact ? formatPhone(dispatch.HRContact.phone) || dispatch.HRContact.phone : '—'}
          {dispatch.HRContact && !dispatch.HRContact.isPhoneValid && (
            <CurecallTooltip
              content={t('pages.hr.campaigns.detail.dispatches-table.invalid-phone-tooltip')}
              tooltipPosition="right"
              wrapperClassName="c-flex"
            >
              <AlertIcon className="c-h-4 c-w-4 c-text-red-500 c-shrink-0" />
            </CurecallTooltip>
          )}
        </span>
      ),
      Status: {
        observedData: dispatch.status,
        component:
          dispatch.status === EHRDispatchStatus.SUPERSEDED ? (
            <CurecallTooltip
              content={t('pages.hr.campaigns.detail.dispatch-status.superseded-tooltip')}
              tooltipPosition="top"
              wrapperClassName="c-h-max"
            >
              {statusBadge}
            </CurecallTooltip>
          ) : (
            statusBadge
          ),
      },
      Actions: (
        <CurecallButton
          appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
          size={ECurecallSize.SMALL}
          onClick={() => setSelectedDispatch(dispatch)}
        >
          {t('pages.hr.campaigns.detail.dispatches-table.read-sms')}
        </CurecallButton>
      ),
    }
  })

  const headers: CurecallListHeaders<TTableRow> = [
    {
      label: t('pages.hr.contacts.table.lastname'),
      key: 'Lastname',
      isSorted: true,
      onHeaderClick: () => handleSort('Lastname'),
    },
    {
      label: t('pages.hr.contacts.table.firstname'),
      key: 'Firstname',
      isSorted: true,
      onHeaderClick: () => handleSort('Firstname'),
    },
    { label: t('pages.hr.campaigns.detail.dispatches-table.phone'), key: 'Phone' },
    {
      label: t('pages.hr.campaigns.detail.dispatches-table.status'),
      key: 'Status',
      isSorted: true,
      onHeaderClick: () => handleSort('Status'),
    },
    { label: '', key: 'Actions' },
  ]

  const paginatedRows = rows.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  return (
    <>
      <div className="c-flex-1 c-min-h-0 c-overflow-y-auto">
        <CurecallList rowsData={paginatedRows} headersData={headers} currentOrder={sort} />
      </div>
      {rows.length > ITEMS_PER_PAGE && (
        <CurecallPaginationBar
          totalItems={rows.length}
          itemsPerPage={ITEMS_PER_PAGE}
          currentPage={currentPage}
          numberOfVisiblePages={7}
          pageLabel={t('common.actions.page')}
          pagesLabel={t('common.actions.pages')}
          onClick={(page) => setCurrentPage(page)}
        />
      )}
      {selectedDispatch && (
        <DispatchSmsLogsModal
          dispatch={selectedDispatch}
          campaignId={campaignId}
          onClose={() => setSelectedDispatch(null)}
        />
      )}
    </>
  )
}
